import { useEffect, useState } from "react";
import {
  Building2, MapPin, Package, FileEdit, Inbox, Layers, Loader2, ChevronRight,
  ArrowLeft, ShieldAlert,
} from "lucide-react";
import CockpitLayout from "@/components/admin/CockpitLayout";
import { api } from "@/lib/api";

const GOLD = "#D89A00", INK = "#2C2E33", SLATE = "#777F88", MUTED = "#5B6472", LINE = "#E7E1D6";

function Kpi({ label, value, icon: Icon, testid, emphasize }) {
  return (
    <div className="fundio-panel" style={emphasize ? { borderColor: "rgba(216,154,0,.45)" } : undefined} data-testid={testid}>
      <span className="flex h-10 w-10 items-center justify-center rounded-xl" style={{ background: "rgba(216,154,0,.10)", color: GOLD }}><Icon size={18} /></span>
      <p className="mt-4 text-[34px] font-black leading-none tabular-nums" style={{ color: INK }}>{value}</p>
      <p className="mt-2 text-[13.5px] font-semibold" style={{ color: INK }}>{label}</p>
    </div>
  );
}

export default function MultiDashboard() {
  const [org, setOrg] = useState(null);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => {
    Promise.allSettled([api.get("/org/current"), api.get("/locations/summary")]).then(([o, s]) => {
      if (o.status === "fulfilled" && o.value.data.status === "ok") setOrg(o.value.data.tenant);
      if (s.status === "fulfilled") setSummary(s.value.data);
      setLoading(false);
    });
  };
  useEffect(load, []);

  const selectLocation = async (locId) => {
    try { await api.post("/session/select-location", { location_id: locId }); window.location.reload(); }
    catch { /* ignore */ }
  };

  const inquiriesNew = summary?.totals?.inquiries_new ?? 0;
  const canViewAll = !!summary?.can_view_all;
  const activeLocId = summary?.active_location_id || null;
  // non-central users never see an "Alle Standorte" aggregate — default to their location
  const effectiveLocId = activeLocId || (!canViewAll ? summary?.locations?.[0]?.id : null);
  const activeLoc = summary?.locations?.find((l) => l.id === effectiveLocId) || null;
  const isCentral = canViewAll && !activeLocId;

  return (
    <CockpitLayout active="dashboard" title="Dashboard" inquiriesNew={inquiriesNew}>
      <div className="fundio-page-stack">
        {/* context header */}
        <div className="flex flex-wrap items-end justify-between gap-4 pb-6" style={{ borderBottom: `1px solid ${LINE}` }}>
          <div>
            <p className="flex items-center gap-2 text-[12px] font-bold uppercase tracking-[0.14em]" style={{ color: GOLD }}>
              <Building2 size={14} /> {org?.company_name || "…"}
              {org?.internal_qa && (
                <span className="rounded-full bg-fundio-slate/10 px-2 py-0.5 text-[10px] font-bold tracking-normal" style={{ color: SLATE }}>INTERNAL QA</span>
              )}
            </p>
            <h1 className="fundio-page-title mt-2 flex items-center gap-2.5" style={{ color: INK }} data-testid="multi-dashboard-title">
              {isCentral ? <><Layers size={26} style={{ color: GOLD }} /> Alle Standorte</> : <><MapPin size={26} style={{ color: GOLD }} /> {activeLoc?.name || "Standort"}</>}
            </h1>
            <p className="mt-2 fundio-muted text-[14px]">
              {isCentral ? "Zentrale Übersicht über alle deine Brocki-Standorte." : `Standort-Kontext${activeLoc?.city ? " · " + activeLoc.city : ""}. Module reagieren künftig auf diesen Standort.`}
            </p>
          </div>
          {!isCentral && summary?.can_view_all && (
            <button onClick={() => selectLocation(null)} data-testid="back-to-central" className="fundio-action">
              <ArrowLeft size={15} /> Alle Standorte
            </button>
          )}
        </div>

        {loading ? (
          <div className="flex justify-center py-20"><Loader2 className="animate-spin" style={{ color: GOLD }} /></div>
        ) : !summary || (summary.locations || []).length === 0 ? (
          <div className="fundio-panel border-dashed py-16 text-center" data-testid="multi-empty">
            <span className="mx-auto flex h-12 w-12 items-center justify-center rounded-full" style={{ background: "rgba(216,154,0,.10)" }}><ShieldAlert style={{ color: GOLD }} size={22} /></span>
            <p className="mt-3 text-[15px] font-bold" style={{ color: INK }}>Kein Standort-Zugriff</p>
            <p className="mt-1 text-[13px]" style={{ color: SLATE }}>Dir ist noch kein Standort zugewiesen.</p>
          </div>
        ) : (
          <>
            {/* KPIs */}
            <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
              {isCentral && <Kpi testid="kpi-standorte" label="Standorte" value={summary.locations_count} icon={Building2} />}
              <Kpi testid="kpi-aktive" label="Aktive Fundstücke" value={(isCentral ? summary.totals.available : activeLoc?.available) ?? 0} icon={Package} />
              <Kpi testid="kpi-entwuerfe" label="Entwürfe" value={(isCentral ? summary.totals.draft : activeLoc?.draft) ?? 0} icon={FileEdit} />
              <Kpi testid="kpi-anfragen" label="Offene Anfragen" value={(isCentral ? summary.totals.inquiries_new : activeLoc?.inquiries_new) ?? 0} icon={Inbox} emphasize={inquiriesNew > 0} />
            </div>

            {/* location list (central view) */}
            {isCentral && (
              <div className="fundio-panel overflow-hidden" style={{ padding: 0 }} data-testid="location-list">
                <div className="flex items-center justify-between border-b px-5 py-4" style={{ borderColor: LINE }}>
                  <h2 className="text-[16px] font-bold" style={{ color: INK }}>Standorte</h2>
                  <span className="text-[12.5px]" style={{ color: SLATE }}>{summary.locations_count} Standort(e)</span>
                </div>
                <div className="hidden grid-cols-[1.6fr_1fr_0.8fr_0.9fr_0.8fr] gap-3 px-5 py-2.5 text-[11px] font-semibold uppercase tracking-wide sm:grid" style={{ color: SLATE, background: "#FBFAF7" }}>
                  <span>Standort</span><span>Ort</span><span className="text-right">Fundstücke</span><span className="text-right">Offene Anfragen</span><span className="text-right">Status</span>
                </div>
                {summary.locations.map((l) => (
                  <button key={l.id} onClick={() => selectLocation(l.id)} data-testid={`location-row-${l.id}`}
                    className="grid w-full grid-cols-2 items-center gap-3 border-t px-5 py-3.5 text-left transition-colors hover:bg-[#F7F5F0] sm:grid-cols-[1.6fr_1fr_0.8fr_0.9fr_0.8fr]" style={{ borderColor: LINE }}>
                    <span className="flex items-center gap-2.5 min-w-0">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-fundio-gold/10"><MapPin size={16} className="text-fundio-gold" /></span>
                      <span className="min-w-0">
                        <span className="block truncate text-[14px] font-semibold" style={{ color: INK }}>{l.name}</span>
                        {l.is_primary && <span className="text-[11px] font-medium" style={{ color: GOLD }}>Hauptstandort</span>}
                      </span>
                    </span>
                    <span className="text-[13px]" style={{ color: MUTED }}>{l.city || "—"}</span>
                    <span className="text-right text-[14px] font-bold tabular-nums" style={{ color: INK }}>{l.available}</span>
                    <span className="text-right text-[14px] font-bold tabular-nums" style={{ color: l.inquiries_new > 0 ? GOLD : INK }}>{l.inquiries_new}</span>
                    <span className="flex items-center justify-end gap-1">
                      <span className="rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase tracking-wide" style={{ background: "#E7F6EC", color: "#1E7E45" }}>Aktiv</span>
                      <ChevronRight size={16} className="hidden shrink-0 sm:block" style={{ color: SLATE }} />
                    </span>
                  </button>
                ))}
              </div>
            )}

            {/* single-location context detail */}
            {!isCentral && activeLoc && (
              <div className="fundio-panel" data-testid="location-detail">
                <h2 className="flex items-center gap-2 text-[16px] font-bold" style={{ color: INK }}><MapPin size={17} style={{ color: GOLD }} /> {activeLoc.name}</h2>
                <p className="mt-1 text-[13px]" style={{ color: MUTED }}>{activeLoc.city || ""} · {activeLoc.is_primary ? "Hauptstandort" : "Filiale"}</p>
                <p className="mt-4 text-[13px]" style={{ color: SLATE }}>
                  Standortbezogene Module (Fundstücke, Anfragen, Öffnungszeiten) reagieren in der nächsten Phase auf diesen Kontext.
                </p>
              </div>
            )}
          </>
        )}
      </div>
    </CockpitLayout>
  );
}
